import {
  capRemaining,
  currentSegment,
  repsFromProgress,
  totalReps,
  type WodSegment,
} from '@timecap/core';

/**
 * Donde se quedo el atleta.
 *
 * Un toque, no una casilla por movimiento. Tocas el ultimo que completo y
 * lo de arriba se da por hecho: en un 21-15-9 son seis casillas contra un
 * toque, y el juez esta de pie con el atleta delante.
 *
 * El gesto es el mismo en vivo que al final del heat, asi que el juez
 * aprende una sola cosa. Y quien termina dentro del cap no toca nada de
 * esto: su marca es el tiempo.
 */
export function SegmentPicker({
  segments,
  segmentsDone,
  partialReps,
  esLaMarca,
  onChange,
}: {
  segments: WodSegment[];
  segmentsDone: number;
  partialReps: number;
  /**
   * Si este avance ES la marca del atleta (llego al cap) o solo por donde
   * va (sigue compitiendo).
   *
   * Cambia lo que se lee abajo: un CAP+18 en mitad del WOD seria mentira
   * --todavia puede terminar-- y un "va por 72 de 90" tras el corte no
   * dice lo que se va a publicar.
   */
  esLaMarca: boolean;
  onChange: (segmentsDone: number, partialReps: number) => void;
}) {
  if (segments.length === 0) return null;

  const enCurso = currentSegment(segments, segmentsDone);
  const reps = repsFromProgress(segments, { segmentsDone, partialReps });
  const total = totalReps(segments);
  const faltan = capRemaining(segments, reps);

  return (
    <div className="segs">
      <p className="segs-q">{esLaMarca ? '¿Dónde se quedó?' : '¿Por dónde va?'}</p>

      <ol className="segs-list">
        {segments.map((seg, i) => {
          const hecho = i < segmentsDone;
          const actual = i === segmentsDone;
          return (
            <li key={seg.sort_order}>
              <button
                type="button"
                className={`seg ${hecho ? 'is-done' : ''} ${actual ? 'is-current' : ''}`}
                // Tocar un bloque = lo completo. Tocar el que ya estaba
                // marcado como completo lo deshace, que es como se corrige
                // un toque de mas sin buscar otro control.
                onClick={() => onChange(hecho && i === segmentsDone - 1 ? i : i + 1, 0)}
              >
                <span className="seg-n">{seg.reps}</span>
                <span className="seg-m">
                  {seg.movement}
                  {seg.load_kg != null && <small> {seg.load_kg} kg</small>}
                </span>
                {hecho && <span className="seg-tick">✓</span>}
              </button>
            </li>
          );
        })}
      </ol>

      {/* El caso frecuente: se quedo a media serie. */}
      {enCurso && (
        <div className="segs-partial">
          <p className="segs-q">
            De {enCurso.reps} {enCurso.movement.toLowerCase()}, ¿cuántas hizo?
          </p>
          <div className="segs-stepper">
            <button
              type="button"
              onClick={() => onChange(segmentsDone, Math.max(0, partialReps - 1))}
              aria-label="Una rep menos"
            >
              −
            </button>
            <span className="segs-count">
              <strong>{Math.min(partialReps, enCurso.reps)}</strong>
              <small> de {enCurso.reps}</small>
            </span>
            <button
              type="button"
              // Al llegar al tope, el bloque esta completo: se marca como
              // tal en vez de dejar un parcial que vale lo mismo.
              onClick={() =>
                partialReps + 1 >= enCurso.reps
                  ? onChange(segmentsDone + 1, 0)
                  : onChange(segmentsDone, partialReps + 1)
              }
              aria-label="Una rep más"
            >
              +
            </button>
          </div>
        </div>
      )}

      <p className="segs-total">
        <span>
          {reps} de {total} reps
        </span>
        {/* El CAP+N solo se nombra cuando de verdad es su marca. */}
        {!esLaMarca ? (
          <strong>{faltan > 0 ? 'en curso' : 'todo hecho'}</strong>
        ) : faltan > 0 ? (
          <strong className="segs-cap">CAP+{faltan}</strong>
        ) : (
          <strong>completo</strong>
        )}
      </p>
    </div>
  );
}
